/**
 * 인증이 필요한 라우트를 보호하는 컴포넌트
 */

import { ReactNode, useEffect, useState, useMemo, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth, useIsAuthenticated, useAuthLoading } from '../../hooks/useAuth';
import { designTokens } from '../../design-system/tokens';
import { STORAGE_KEYS } from '../../utils/constants';

/**
 * Protected Route Props
 */
interface ProtectedRouteProps {
  /** 보호할 컴포넌트 */
  children: ReactNode;

  /** 필요한 권한 (선택사항) */
  requiredPermissions?: string[];

  /** 권한이 없을 때 리다이렉트할 경로 (기본: /unauthorized) */
  unauthorizedRedirect?: string;
}

/**
 * 저장소에 토큰이 있는지 확인
 */
const hasStoredTokens = (): boolean => {
  return !!(
    localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
    sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  );
};

/**
 * JWT 토큰에서 권한 정보 추출 (동기적 호출)
 */
const getPermissionsFromToken = (): string[] => {
  try {
    const token =
      localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) ||
      sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    if (!token) return [];

    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) return [];

    const payload = JSON.parse(atob(tokenParts[1]));
    if (payload.auth) {
      return typeof payload.auth === 'string'
        ? payload.auth.split(',').map((r: string) => r.trim())
        : [];
    }
    return [];
  } catch (e) {
    console.error('[ProtectedRoute] JWT 파싱 오류:', e);
    return [];
  }
};

/**
 * Protected Route 컴포넌트
 *
 * 인증되지 않은 사용자는 로그인 페이지로 리다이렉트
 * 권한이 없는 사용자는 unauthorizedRedirect 경로로 리다이렉트
 */
export const ProtectedRoute = ({
  children,
  requiredPermissions = [],
  unauthorizedRedirect = '/unauthorized',
}: ProtectedRouteProps) => {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthLoading();
  const { user, initialize } = useAuth();
  const [isInitializing, setIsInitializing] = useState(false);
  const initAttemptedRef = useRef(false);

  /**
   * 앱 시작 시 인증 상태 초기화 (1회만 시도)
   */
  useEffect(() => {
    if (!isAuthenticated && !isLoading && hasStoredTokens() && !initAttemptedRef.current) {
      initAttemptedRef.current = true;
      setIsInitializing(true);
      initialize();
    }
  }, [isAuthenticated, isLoading, initialize]);

  /**
   * 초기화 완료 후 상태 업데이트
   */
  useEffect(() => {
    if (isInitializing && !isLoading) {
      setIsInitializing(false);
    }
  }, [isInitializing, isLoading]);

  /**
   * 동기적 권한 체크 (JWT에서 직접 추출)
   */
  const permissionCheck = useMemo(() => {
    // 권한 체크가 필요없으면 패스
    if (requiredPermissions.length === 0) {
      return { needsCheck: false, hasPermission: true };
    }

    // 토큰이 없으면 체크 불가
    if (!hasStoredTokens()) {
      return { needsCheck: false, hasPermission: true };
    }

    // JWT에서 직접 권한 확인
    const tokenPermissions = getPermissionsFromToken();
    const hasPermission = requiredPermissions.every(permission =>
      tokenPermissions.includes(permission)
    );

    console.log('[ProtectedRoute] 동기적 권한 체크:', {
      path: location.pathname,
      required: requiredPermissions,
      tokenPermissions,
      hasPermission,
    });

    return { needsCheck: true, hasPermission };
  }, [requiredPermissions, location.pathname]);

  // 디버그 로깅
  console.log('[ProtectedRoute] 렌더링 상태:', {
    path: location.pathname,
    isLoading,
    isInitializing,
    isAuthenticated,
    hasTokens: hasStoredTokens(),
    permissionCheck,
    requiredPermissions,
  });

  /**
   * 🔴 핵심 수정: 권한 체크를 로딩 조건보다 먼저 수행
   * 토큰이 있고 권한이 없으면 즉시 리다이렉트 (Zustand 상태와 무관하게)
   */
  if (permissionCheck.needsCheck && !permissionCheck.hasPermission) {
    console.log('[ProtectedRoute] 권한 없음 - 즉시 리다이렉트:', unauthorizedRedirect);
    return <Navigate to={unauthorizedRedirect} replace />;
  }

  /**
   * 토큰이 없는 경우 로그인 페이지로 리다이렉트
   */
  if (!hasStoredTokens()) {
    console.log('[ProtectedRoute] 토큰 없음 - 로그인으로');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /**
   * 로딩 중이거나 초기화 중일 때 로딩 스피너 표시
   * (권한이 있는 사용자만 여기에 도달)
   */
  if (
    isLoading ||
    isInitializing ||
    (!isAuthenticated && hasStoredTokens() && !initAttemptedRef.current)
  ) {
    console.log('[ProtectedRoute] 로딩 중...');
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: designTokens.colors.functional.background,
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: designTokens.colors.brand.primary,
          }}
        />
      </Box>
    );
  }

  /**
   * 인증되지 않은 경우 로그인 페이지로 리다이렉트
   */
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] 인증 안됨 - 로그인으로');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /**
   * 인증되고 권한이 있는 경우 자식 컴포넌트 렌더링
   */
  console.log('[ProtectedRoute] 권한 있음 - children 렌더링');
  return <>{children}</>;
};

export default ProtectedRoute;
